
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FoodItem, Language } from '../types';
import { t } from '../locales';
import { searchFoods } from '../constants/autocompleteData';
import { QuantityEditModal } from './QuantityEditModal';
import { Trash2, Plus, Info, X, Star, Users } from 'lucide-react';

interface RecipeModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: FoodItem;
  onSave: (updatedItem: FoodItem, saveAsCustom: boolean, servings?: number) => void;
  language: Language;
  isNameReadOnly?: boolean;
  hideSaveOption?: boolean; 
  theme?: 'blue' | 'yellow';
}

const RecipeModal: React.FC<RecipeModalProps> = ({ 
    isOpen, 
    onClose, 
    item, 
    onSave, 
    language, 
    isNameReadOnly = false,
    hideSaveOption = false,
    theme = 'blue'
}) => {
  const [ingredients, setIngredients] = useState<FoodItem[]>(item.ingredients || []);
  const [newItemName, setNewItemName] = useState('');
  const [recipeName, setRecipeName] = useState(item.name);
  const [servings, setServings] = useState<number>(item.quantity || 4);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [saveAsCustom, setSaveAsCustom] = useState(hideSaveOption ? true : false);
  
  // State for quantity sub-modal
  const [editingIngredient, setEditingIngredient] = useState<FoodItem | null>(null);

  const isYellow = theme === 'yellow';

  useEffect(() => {
      setRecipeName(item.name);
      setIngredients(item.ingredients || []);
      setServings(item.quantity || 4);
  }, [item]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setNewItemName(val);
      if (val.length > 1) {
          const matches = searchFoods(val, language, 5);
          setSuggestions(matches);
      } else {
          setSuggestions([]);
      }
  };

  const addIngredient = (name: string) => {
      const newIng: FoodItem = {
          id: crypto.randomUUID(),
          name: name,
          quantity: 1, 
          unit: 'unit', 
          timestamp: new Date()
      };
      setIngredients([...ingredients, newIng]);
      setNewItemName('');
      setSuggestions([]);
  };

  const removeIngredient = (id: string) => {
      setIngredients(ingredients.filter(i => i.id !== id));
  };

  const handleUpdateQuantity = (qty: number, unit: string) => {
      if (!editingIngredient) return;
      setIngredients(ingredients.map(i => 
          i.id === editingIngredient.id ? { ...i, quantity: qty, unit } : i
      ));
      setEditingIngredient(null);
  };

  const handleSave = () => {
      const updatedItem: FoodItem = {
          ...item,
          name: recipeName, 
          quantity: servings, // In this modal context, quantity represents servings
          ingredients: ingredients,
          isComposite: ingredients.length > 0
      };
      onSave(updatedItem, hideSaveOption ? true : saveAsCustom, servings);
      onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl shadow-2xl p-6 relative z-10 animate-popIn border border-gray-100 dark:border-gray-700 flex flex-col max-h-[85vh]">
        
        <div className="flex justify-between items-start mb-4">
            <div className="flex-1 mr-4">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    {t(language, 'recipeNamePlaceholder')}
                </label>
                {isNameReadOnly ? (
                    <div className="text-lg font-bold text-gray-800 dark:text-white border-b border-transparent w-full pb-1 flex items-center gap-2">
                        <span className={`truncate ${isYellow ? 'text-yellow-600 dark:text-yellow-400' : 'text-[#3F88C5]'}`}>{recipeName}</span>
                        <div className={`${isYellow ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-[#3F88C5]/10'} p-1 rounded-md`} title="Template">
                            <Star size={12} className={isYellow ? 'text-yellow-600' : 'text-[#3F88C5]'} fill="currentColor" />
                        </div>
                    </div>
                ) : (
                    <input 
                        type="text"
                        value={recipeName}
                        onChange={(e) => setRecipeName(e.target.value)}
                        className={`text-lg font-bold text-gray-800 dark:text-white bg-transparent border-b outline-none w-full pb-1 border-gray-200 dark:border-gray-600 focus:border-${isYellow ? 'yellow-500' : '[#3F88C5]'}`}
                        placeholder={t(language, 'recipeNamePlaceholder')}
                        autoFocus={item.id === 'new'}
                    />
                )}
            </div>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                <X size={24} />
            </button>
        </div>

        {/* Servings Counter */}
        <div className="flex items-center gap-4 mb-4 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
            <div className={`p-2 rounded-lg ${isYellow ? 'bg-yellow-100 text-yellow-600' : 'bg-blue-100 text-blue-600'}`}>
                <Users size={18} />
            </div>
            <div className="flex-1">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">{t(language, 'servings')}</span>
                <div className="flex items-center gap-3 mt-1">
                    <button onClick={() => setServings(Math.max(1, servings - 1))} className="w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center font-bold text-gray-600 dark:text-gray-300">-</button>
                    <span className="text-sm font-black text-gray-800 dark:text-white w-4 text-center">{servings}</span>
                    <button onClick={() => setServings(servings + 1)} className="w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center font-bold text-gray-600 dark:text-gray-300">+</button>
                </div>
            </div>
        </div>

        <div className="flex-1 overflow-y-auto mb-4 border rounded-xl border-gray-100 dark:border-gray-700 min-h-0">
            {ingredients.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm italic">
                    No items listed. Add some below.
                </div>
            ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {ingredients.map((ing) => (
                        <div key={ing.id} className="p-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            {/* Interactive Quantity/Unit Button */}
                            <button 
                                type="button"
                                onClick={() => setEditingIngredient(ing)}
                                className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-all border flex items-center gap-1 min-w-[70px] justify-center shadow-sm ${
                                    isYellow 
                                    ? 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100' 
                                    : 'bg-blue-50 border-blue-100 text-[#3F88C5] hover:bg-blue-100'
                                }`}
                            >
                                <span>{ing.quantity}</span>
                                <span className="opacity-60 font-medium truncate max-w-[40px]">{ing.unit}</span>
                            </button>

                            <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200 capitalize truncate">{ing.name}</span>
                            
                            <button onClick={() => removeIngredient(ing.id)} className="text-gray-300 hover:text-red-500 p-1 transition-colors">
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>

        <div className="relative mb-4">
            <div className="flex gap-2">
                <input 
                    type="text" 
                    value={newItemName}
                    onChange={handleNameChange}
                    placeholder="Add ingredient (e.g. Flour)"
                    className={`flex-1 p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm focus:ring-2 focus:ring-${isYellow ? 'yellow-500' : '[#3F88C5]'} outline-none`}
                    onKeyDown={(e) => { if(e.key === 'Enter' && newItemName) addIngredient(newItemName); }}
                />
                <button 
                    onClick={() => newItemName && addIngredient(newItemName)}
                    className={`w-12 h-12 flex items-center justify-center rounded-xl transition-transform active:scale-95 ${isYellow ? 'bg-yellow-500 text-white shadow-lg shadow-yellow-100' : 'bg-[#3F88C5] text-white shadow-lg shadow-blue-100'}`}
                >
                    <Plus size={20} strokeWidth={3} />
                </button>
            </div>
            {suggestions.length > 0 && (
                <div className="absolute bottom-full left-0 w-full bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-600 rounded-lg shadow-xl mb-2 overflow-hidden z-20 animate-popIn">
                    {suggestions.map((s, i) => (
                        <button key={i} onClick={() => addIngredient(s)} className={`block w-full text-left px-4 py-3 text-sm hover:bg-${isYellow ? 'yellow-50' : 'blue-50'} dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 border-b last:border-0 border-gray-50 dark:border-gray-700 transition-colors`}>
                            {s}
                        </button>
                    ))}
                </div>
            )}
        </div>

        {!hideSaveOption && (
            <div className="flex items-center gap-2 mb-4 bg-gray-50 dark:bg-gray-900/50 p-2 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
                <input 
                    type="checkbox" 
                    id="saveCustom" 
                    checked={saveAsCustom} 
                    onChange={(e) => setSaveAsCustom(e.target.checked)}
                    className={`w-4 h-4 rounded border-gray-300 ${isYellow ? 'text-yellow-500 focus:ring-yellow-500' : 'text-[#3F88C5] focus:ring-[#3F88C5]'}`}
                />
                <label htmlFor="saveCustom" className="text-[10px] text-gray-500 dark:text-gray-400 font-medium leading-tight">
                    Remember this recipe for <strong>{recipeName || "this dish"}</strong>
                </label>
            </div>
        )}

        <button 
            onClick={handleSave}
            disabled={!recipeName.trim()}
            className={`w-full py-4 rounded-xl ${isYellow ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-[#3F88C5] hover:bg-[#3F88C5]/90'} text-white font-bold shadow-lg transition-transform active:scale-95 disabled:opacity-50`}
        >
            {t(language, 'save')}
        </button>

        {/* Quantity Pop-up for single ingredients */}
        {editingIngredient && (
            <QuantityEditModal 
                isOpen={!!editingIngredient}
                onClose={() => setEditingIngredient(null)}
                currentItem={editingIngredient}
                onSave={handleUpdateQuantity}
                language={language}
                theme={isYellow ? 'yellow' : 'emerald'} 
            />
        )}

      </div>
    </div>,
    document.body
  );
};

export default RecipeModal;
